/* eslint-disable-next-line local/require-storybook-story --
 * On mount this opens a real WebSocket (SocketStreaming.connectToServer) and issues live
 * device-connection and start-streaming requests (fetchCheckDeviceConnection,
 * fetchPushStartEcgStreaming) — same real-time pipeline as the fabricJs EcgViewer it wraps
 * (see that file's own disable comment), which no fixture can stand in for. Covered by
 * E2E/manual verification, not Storybook. */
import { ConnectionErrorIcon, generateIcon, infoIconV2 } from '@/shared/ui/assets';
import { DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import { CustomButton } from '@/shared/ui/components';
import { withModalLayout } from '@/shared/lib';
import { forwardRef } from 'react';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { useViewLiveEcgModal } from './useViewLiveEcgModal';
import EcgViewer from './fabricJs/ecgViewer';
import type { ViewLiveEcgModalProps } from './viewLiveEcgModal.types';
import './viewLiveEcgModal.scss';

/**
 * Modal streaming a study's live ECG. Shows a cellular five-minute limit notice when shouldStreamViaCellular, then branches on useViewLiveEcgModal state between a stopped-stream panel with Restart, a connection-error panel with Retry, a loading placeholder and EcgViewer fed from the streaming buffer, next to the patient title blocks and the live bpm readout.
 *
 * @param props - Modal props including the study whose ECG is streamed.
 * @param ref - Ref exposing the modal imperative handle.
 * @returns The live ECG modal component.
 */
const ViewLiveEcgModal = withModalLayout(
  forwardRef((props: ViewLiveEcgModalProps, ref) => {
    const {
      state,
      shouldStreamViaCellular,
      restartEcgStreaming,
      handleRetryClick,
      debounceOnCalculatedData,
      onBpmChanged,
      ecgStreamingDataBuffer,
      patientInfo,
    } = useViewLiveEcgModal(props, ref);

    return (
      <>
        {shouldStreamViaCellular && (
          <div className="info-message-container">
            <ReactSVG src={infoIconV2} />
            {t`Live ECG streaming over cellular is limited to a maximum of 5 minutes to conserve data usage.`}
          </div>
        )}

        <div className="custom-modal-content">
          {state.isConnectionError ? (
            state.isStoppedLiveEcgViaCellular ? (
              <div className="connection-error-container">
                <ReactSVG src={ConnectionErrorIcon} aria-hidden="true" />
                <span className="message">
                  {t`Live ECG streaming has stopped. To continue viewing, please restart.`}
                </span>
                <CustomButton
                  id="restart-ecg-streaming-btn"
                  className="restart-ecg-streaming-btn"
                  size="small"
                  type="outline"
                  onClick={restartEcgStreaming}
                >
                  {t`Restart`}
                </CustomButton>
              </div>
            ) : (
              <div className="connection-error-container">
                <ReactSVG src={ConnectionErrorIcon} aria-hidden="true" />
                <span className="message">
                  {t`Unable to start streaming ECG because there may be an issue with the connection to the device.`}
                </span>
                <CustomButton
                  className="retry-btn"
                  size="small"
                  type="outline"
                  id="retry-check-study-device-connection-btn"
                  onClick={handleRetryClick}
                  disabled={state.isRetrying}
                  loading={state.isRetrying}
                >
                  {t`Retry`}
                </CustomButton>
              </div>
            )
          ) : state.isLoadingEcg ? (
            <div className="loading-ecg-container">
              <div className="message-container">
                <img className="generating-icon" src={generateIcon} alt="generateIcon" />
                <div className="message">{t`Loading ECG, please wait...`}</div>
              </div>
            </div>
          ) : (
            <EcgViewer
              ecg={state.ecgObject}
              configChart={state.configChart}
              index={0}
              isStreaming
              streamingData={ecgStreamingDataBuffer.current}
              onCalculatedData={debounceOnCalculatedData}
              onBpmChanged={onBpmChanged}
            />
          )}

          <div className="patient-information-container">
            <div className="title-block-container">
              {patientInfo.map((element) => (
                <div key={element.title} className="title-block">
                  <div className="title-block__title">{element.title}</div>
                  <div className="item-content">
                    <div className="title-block__content">{element.content}</div>
                  </div>
                </div>
              ))}
            </div>

            {!state.isLoadingEcg && !state.isRetrying && !state.isConnectionError && (
              <div className="heart-rate-container">
                <span className="heart-rate__value">
                  {!Number.isNaN(state.bpm) ? state.bpm : DOUBLE_HYPHEN_BULLET}
                </span>
                <div className="heart-rate__unit">{t`bpm`}</div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }),
);

(ViewLiveEcgModal as any).defaultProps = {
  width: 993,
};

export default ViewLiveEcgModal;
